/**
 * TenantStatusChangeModal Component
 *
 * Modal for requesting tenant status changes (pause/archive).
 * Organization owners can use this to request status changes for tenants
 * in their organization. The request goes to the pending requests queue
 * for global admins to approve.
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Dropdown,
  Option,
  Label,
  Textarea,
  Text,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AsyncButton } from '../common/AsyncButton';
import { Alert } from '../common/Alert';
import { TenantRequestApiService } from '../../services/tenantRequestApi';
import type { OrganizationTenant } from '../../types/organization';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  tenantInfo: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: tokens.borderRadiusMedium,
  },
  tenantName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
  },
  tenantDomain: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
});

interface TenantStatusChangeModalProps {
  isOpen: boolean;
  tenant: OrganizationTenant;
  onClose: () => void;
  onSuccess: () => void;
}

type RequestableStatus = 'paused' | 'archived';

const STATUS_OPTIONS: Array<{ value: RequestableStatus; label: string; description: string }> = [
  {
    value: 'paused',
    label: 'Paused',
    description: 'Temporarily suspend tenant access',
  },
  {
    value: 'archived',
    label: 'Archived',
    description: 'Permanently archive the tenant',
  },
];

export function TenantStatusChangeModal({
  isOpen,
  tenant,
  onClose,
  onSuccess,
}: TenantStatusChangeModalProps) {
  const styles = useStyles();

  const [selectedStatus, setSelectedStatus] = useState<RequestableStatus | null>(null);
  const [justification, setJustification] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedStatus(null);
      setJustification('');
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  // Validation: both status and justification are required
  const isFormValid = selectedStatus !== null && justification.trim().length > 0;

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setError(null);
    setIsSubmitting(true);

    try {
      await TenantRequestApiService.createStatusChangeRequest({
        target_tenant_id: tenant.id,
        requested_status: selectedStatus,
        notes: justification.trim(),
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create status change request');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={(_, data) => !data.open && onClose()}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Request Status Change</DialogTitle>
          <DialogContent className={styles.content}>
            {error && <Alert intent="error">{error}</Alert>}

            {/* Tenant Info (read-only) */}
            <div className={styles.tenantInfo}>
              <Text className={styles.tenantName}>{tenant.tenant_name}</Text>
              <Text className={styles.tenantDomain} block>
                {tenant.domain || tenant.tenant_id}
              </Text>
            </div>

            {/* Status Selection */}
            <div className={styles.field}>
              <Label htmlFor="status-dropdown" required>
                Select new status
              </Label>
              <Dropdown
                id="status-dropdown"
                placeholder="Select a status..."
                selectedOptions={selectedStatus ? [selectedStatus] : []}
                onOptionSelect={(_, data) => {
                  if (data.optionValue) {
                    setSelectedStatus(data.optionValue as RequestableStatus);
                  }
                }}
              >
                {STATUS_OPTIONS.map((option) => (
                  <Option key={option.value} value={option.value} text={option.label}>
                    <div>
                      <Text weight="semibold">{option.label}</Text>
                      <Text size={200} style={{ display: 'block', color: tokens.colorNeutralForeground3 }}>
                        {option.description}
                      </Text>
                    </div>
                  </Option>
                ))}
              </Dropdown>
            </div>

            {/* Justification (required) */}
            <div className={styles.field}>
              <Label htmlFor="justification-input" required>Justification</Label>
              <Textarea
                id="justification-input"
                aria-label="Justification"
                placeholder="Provide a justification for this status change request..."
                value={justification}
                onChange={(e) => setJustification(e.target.value)}
                resize="vertical"
                rows={3}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <AsyncButton
              appearance="primary"
              onClick={handleSubmit}
              disabled={!isFormValid || isSubmitting}
              loading={isSubmitting}
              loadingText="Submitting..."
            >
              Submit Request
            </AsyncButton>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
